import request, { AxiosResponse } from "axios";
import { SalesforceEnvironment } from "encharge-domain/lib/helpers/salesforce/salesforceEnvironment";
import { apiBase } from "../../constants";
import { SalesforceHealthResync } from "./persistSalesforceResync";

/**
 * The Salesforce v2 sync's own routes, mounted at /v1/salesforce-sync.
 *
 * The types below mirror what the server answers. The server's side of this is
 * packages/api/src/controllers/salesforce_sync_controller.ts, with the shapes
 * in services/salesforce/v2/health/SalesforceSyncHealth.ts and
 * services/salesforce/v2/settings/SalesforceSyncSettingsView.ts. Nothing here
 * calls Salesforce: the server reads Encharge's own rows.
 */

export const base = `${apiBase}/v1/salesforce-sync`;

export type SalesforceSyncMode = "v1" | "shadow" | "v2";

/** GET /settings. Only what the screens read; the mapping and describe are not used yet. */
export interface SalesforceSyncSettings {
  mode: SalesforceSyncMode;
  companiesEnabled: boolean;
  oneWay: boolean;
  mappingVersion: number;
}

export type SalesforceHealthStatus =
  | "ok"
  | "running"
  | "paused"
  | "reconnect"
  | "error"
  /** A new connection waits for the customer to finish the setup; nothing is read until then */
  | "setup"
  /** No check of Salesforce has finished for over 30 minutes (item 11) */
  | "stalled";

export interface SalesforceHealthState {
  status: SalesforceHealthStatus;
  /** One plain sentence */
  message: string;
  /** paused only */
  reason?: "api_limit";
  /** paused only: when syncing resumes by itself (ISO) */
  until?: string;
  /** error only: when the last run failed (ISO) */
  at?: string;
}

/** Something that is wrong, with Salesforce's own words where Salesforce said it. */
export interface SalesforceHealthProblem {
  object: string;
  message: string;
  code?: string;
  at: string | null;
  since?: string;
  field?: string;
  /**
   * A read that stopped because Salesforce would not let the sync read a field
   * its sync criteria use: that field. Its changes wait until the Salesforce user
   * who connected Encharge can read it again. Older answers have none.
   */
  criteriaField?: string;
  salesforceId?: string;
  personId?: string;
  /** The Encharge company, for a refused write to an Account */
  companyId?: string;
  attempts?: number;
  step?: "record" | "company";
  /** Who said it: Salesforce, or Encharge's own plain sentence */
  source?: "salesforce" | "encharge";
  /** A trigger problem: how many flows wait for the stage or Campaign */
  flowCount?: number;
  /** A step that failed, or a loop that was stopped: the flows and steps involved */
  flows?: { flowId: number | null; stepId: number | null }[];
}

export interface SalesforceHealthProblemList {
  /** All of them; `items` is the newest few */
  total: number;
  items: SalesforceHealthProblem[];
}

export const APPLY_KINDS = [
  "personCreated",
  "personUpdated",
  "fieldChanged",
  "fieldSkipped",
  "unsubscribed",
  "resubscribed",
  "unsubscribeKept",
  "merged",
  "deletedKept",
  "eventEmitted",
  "rowFailed",
  "companyCreated",
  "companyUpdated",
  "companyFieldChanged",
  "companyFieldSkipped",
  "companyDeletedKept",
  "companyFailed",
  "personCompanyFailed",
  "companyMerged",
  "linkAdded",
  "linkRemoved",
  "triggerFired",
  "triggerPeopleStarted",
  "triggerPeopleSkipped",
  /** An Opportunity stage change that still reached nobody in Encharge 30 minutes after it */
  "triggerNobody",
  "loopStopped",
  /** A Lead or Contact with no email: no person was made (item 0) */
  "skippedNoEmail",
  /** The same for a Person Account, on a line of its own */
  "skippedNoEmailPersonAccount",
  /** An Opportunity was stored */
  "opportunityCreated",
  /** A stored Opportunity was changed */
  "opportunityUpdated",
  /** An Opportunity deleted in Salesforce was marked Deleted in Salesforce; it is kept, never removed */
  "opportunityDeleted",
  /** An Opportunity was read and is outside the window or the rules: nothing was written */
  "opportunityOutside",
  /** An Opportunity could not be written */
  "opportunityFailed",
  /** A person or a company was linked to an Opportunity */
  "opportunityLinkAdded",
  /** A person or a company lost its link to an Opportunity */
  "opportunityLinkRemoved",
] as const;
export type SalesforceApplyKind = typeof APPLY_KINDS[number];

export interface SalesforceReportSample<K extends string> {
  object: "Lead" | "Contact" | "Account" | "Opportunity";
  salesforceId: string;
  personId: number | string | null;
  field: string | null;
  salesforceValue: string | null;
  enchargeValue: string | null;
  kind: K;
  reason?: string;
  /** ISO */
  at: string;
}

export interface SalesforceReportReadout<K extends string> {
  /** Start of the first hour the counts cover (ISO) */
  since: string;
  counts: Record<K, number>;
  recordsChecked: number;
  /** newest first */
  samples: SalesforceReportSample<K>[];
}

/**
 * A full read of every Lead or Contact is under way (the first read of a new
 * connection, the pass a switch to v2 owes). Records it has not reached yet are
 * updated when it gets to them; the ones it has read are kept up to date beside
 * it.
 */
export interface SalesforceFullRead {
  objects: ("Lead" | "Contact")[];
  /** When it began (ISO); null before its first page */
  startedAt: string | null;
  /** Changes to the records already read are caught up to this time (ISO); null when not yet */
  changesUpTo: string | null;
}

/**
 * The last change of the Sync criteria (7.7 of the criteria design): who and
 * when, for the "Good to know" line with Restore previous rules. The server
 * sends it for 30 days after the change, and only while the rules stored are
 * the ones that change saved.
 */
export interface SalesforceHealthCriteriaChange {
  /** When the rules were changed (ISO) */
  at: string;
  /** The member's name only, never an email; null when none is stored */
  by: { name: string } | null;
  /** The objects whose rules changed who matches */
  objects: ("Lead" | "Contact")[];
  /** The change put earlier rules back: the action then reads "Undo the restore" */
  restored: boolean;
  /** The rules before the change are not the rules stored now, so there is something to put back */
  restorable: boolean;
}

/** What Encharge holds of Opportunities. */
export interface SalesforceHealthOpportunityCounts {
  stored: number;
  /** Updated at every check: open, or closed inside the window, and matching the rules */
  stillUpdated: number;
  /** Kept as they are, not updated: closed before the window, outside the rules, or deleted in Salesforce. None is ever removed. */
  kept: number;
  personLinks: number;
  companyLinks: number;
}

/**
 * A field Salesforce does not let Encharge read that a live flow watches or
 * filters on. Only the flows that use it are paused; Encharge tries it again
 * every 15 minutes.
 */
export interface SalesforceHealthPausedField {
  /** The Salesforce API name */
  field: string;
  /** refused: Salesforce still refuses it. answered: readable again, the flows resume at the next read. */
  status: "refused" | "answered";
  since: string | null;
  lastTriedAt: string | null;
  nextTryAt: string | null;
  flowCount: number;
  /** The live flows it holds up, by name */
  flows: { flowId: number; name: string }[];
}

/** Opportunities on the Sync Health card. Absent from older answers; null when the server could not tell. */
export interface SalesforceHealthOpportunities {
  /** on: synced. off: the customer switched it off. unavailable: the plan has no Salesforce, or the account is not on the new sync. */
  state: "on" | "off" | "unavailable";
  reason?: "plan" | "not_v2";
  windowMonths: number;
  counts: SalesforceHealthOpportunityCounts | null;
  /** A quiet read under way: the first read, a pass, or the refresh of a field that came back */
  read: {
    kind:
      | "first"
      | "upgrade"
      | "widen"
      | "rules"
      | "baseline"
      | "orgChange"
      | "relink"
      | "refresh";
    startedAt: string | null;
  } | null;
  /** The sync came back after being off: what changed meanwhile is filled in quietly and starts no flow */
  catchingUp: boolean;
  check: {
    /** A live flow waits for Opportunities, so they are checked every minute */
    live: boolean;
    lastCheckedAt: string | null;
    failed: boolean;
    refused: boolean;
  };
  /** Changes waiting for their people to reach Encharge */
  held: { total: number; oldestAt: string | null } | null;
  pausedFields: SalesforceHealthPausedField[];
}

/**
 * A segment flow that uses Campaign data (CR21): an Entered segment or Left
 * segment flow whose segment is made from Campaign Members. It reacts to what the
 * Campaign sync writes after the first read.
 */
export interface SalesforceHealthCampaignFlow {
  flowId: number;
  name: string;
  segmentId: number;
  segmentName: string | null;
  /** enter: Entered segment. leave: Left segment. */
  trigger: "enter" | "leave";
}

/** Check now's cost and state, from the sync's plan for the removal check (CD1, CD6). */
export interface SalesforceHealthCampaignCheck {
  /** There is a check to run: a Campaign has finished its first read */
  available: boolean;
  /** Salesforce calls one check makes */
  calls: number | null;
  /** The Campaigns one check compares */
  campaigns: number | null;
  /** The daily allowance the cost is judged against, and whether Salesforce has said it */
  dailyCalls: number;
  dailyCallsKnown: boolean;
  /** The cost as a share of the day's allowance, 0 to 1 */
  costShare: number | null;
  /** Calls used today when Salesforce has said; null otherwise */
  usedToday: number | null;
  /** Where the day would stand after the check, 0 to 1 and above; null when today's use is not known */
  afterShare: number | null;
  /** Pressing now would be refused: the check would take the day past 80 percent */
  refused: boolean;
  /** A Check now is owed: a second press joins it */
  owed: { requestedAt: string } | null;
  /** A check is under way and goes on at the next run */
  running: boolean;
  /** How the last pressed check ended */
  lastPressed: { endedAt: string; complete: boolean } | null;
}

/** Campaigns on the Sync Health card. Absent from older answers; null when the server could not tell. */
export interface SalesforceHealthCampaigns {
  /** on: copied. off: switched off or nothing chosen, what is stored stays. unavailable: no Salesforce plan, or not on the new sync. */
  state: "on" | "off" | "unavailable";
  reason?: "plan" | "not_v2" | "off" | "nothing_chosen";
  /** What Encharge holds; null when it holds nothing */
  counts: { campaigns: number; members: number } | null;
  /** Members on a record Encharge does not hold, as of the last complete check; null before one */
  recordNotHeld: number | null;
  /** When the last check compared everything (ISO); null before the first */
  lastCheckedAt: string | null;
  /** When a removal shows, with the real wait; null before the sync has worked it out */
  removal: { note: string; intervalMs: number; hourly: boolean } | null;
  check: SalesforceHealthCampaignCheck;
  /** The segment flows that use Campaign data, the first 20, and how many there are in all */
  flows: { total: number; flows: SalesforceHealthCampaignFlow[] };
}

/** A flow, a step or a segment that names a Campaign of the Salesforce the account left. */
export interface SalesforceSwitchScanItem {
  kind: "flow" | "step" | "segment";
  /** The flow (for a flow or a step) or the segment */
  id: number;
  name: string;
  /** The step, for a step */
  stepId?: number;
}

/** What the switch reset found: exact counts, and the first of them. */
export interface SalesforceSwitchScan {
  /** ISO */
  at: string;
  flows: number;
  steps: number;
  segments: number;
  items: SalesforceSwitchScanItem[];
  /** There are more than `items` holds */
  truncated: boolean;
}

/** The people a Sandbox created that Production does not know. */
export interface SalesforceSandboxPeople {
  segment: { id: number; name: string };
  count: number;
}

/** A field the field mapping holds or a sync rule uses that the new Salesforce does not have. */
export interface SalesforceMissingField {
  object:
    | "Lead"
    | "Contact"
    | "Account"
    | "Opportunity"
    | "Campaign"
    | "CampaignMember";
  /** The API name as it was saved */
  field: string;
  /** The field mapping holds it */
  mapped: boolean;
  /** A sync rule uses it */
  rules: boolean;
}

/** After a switch to another Salesforce: the Refresh fields that is owed, and what the new one lacks. */
export interface SalesforceHealthFields {
  /** When the Refresh fields became owed (ISO), or null when none is */
  owed: string | null;
  /** The fields the new Salesforce lacks, the first of them; empty while a Refresh fields is owed */
  missing: SalesforceMissingField[];
  /** How many it lacks in all */
  missingCount: number;
}

export interface SalesforceSyncHealth {
  mode: SalesforceSyncMode;
  oneWay: boolean;
  companiesEnabled: boolean;
  state: SalesforceHealthState;
  /** A full read under way, or null. Older answers have none. */
  fullRead?: SalesforceFullRead | null;
  /** The last change of the Sync criteria, or null. Older answers have none. */
  criteriaChange?: SalesforceHealthCriteriaChange | null;
  /** The latest Resync everything; null before the first one. Older answers have none. */
  resync?: SalesforceHealthResync | null;
  /** Opportunities. Older answers have none. */
  opportunities?: SalesforceHealthOpportunities | null;
  /** Campaigns. Older answers have none. */
  campaigns?: SalesforceHealthCampaigns | null;
  /**
   * After a switch of Salesforce: the flows, steps and segments that name
   * Campaigns of the Salesforce the account left, or null when there are none.
   * Older answers have none.
   */
  switchScan?: SalesforceSwitchScan | null;
  /**
   * After a Sandbox, once Production has read every person: the people the
   * Sandbox created that Production does not know, with the ready-made segment
   * that holds them, or null. Older answers have none.
   */
  sandboxPeople?: SalesforceSandboxPeople | null;
  /**
   * After a switch to another Salesforce: a Refresh fields that is owed, or the
   * mapped and rule fields the new one does not have, or null when there is
   * nothing to say. Older answers have none.
   */
  fields?: SalesforceHealthFields | null;
  counts: {
    leads: number;
    contacts: number;
    /** Of the people with a Contact, the Person Accounts. Older answers have none. */
    personAccounts?: number;
    companies: number;
    companyLinks: number;
  };
  times: {
    lastRunStartedAt: string | null;
    lastRunFinishedAt: string | null;
    nextRunAt: string | null;
    /**
     * When Salesforce was last connected to a different org than the one the
     * sync's cursors came from, while that is within the last 7 days; null
     * otherwise. Older answers have none.
     */
    orgChangedAt?: string | null;
    lastChangeSynced: Partial<Record<string, string | null>>;
  };
  api: {
    used: number | null;
    max: number | null;
    percent: number | null;
    readAt: string | null;
    callsLast24h: number;
  };
  last24Hours: {
    since: string;
    recordsChecked: number;
    counts: Record<SalesforceApplyKind, number>;
    samples: SalesforceReportSample<SalesforceApplyKind>[];
  };
  problems: {
    fieldWarnings: SalesforceHealthProblem[];
    syncErrors: SalesforceHealthProblem[];
    companyProblems: SalesforceHealthProblem[];
    givenUp: SalesforceHealthProblemList;
    optOutRefused: SalesforceHealthProblemList;
    /** Writes of changed fields that Salesforce refused, or that Encharge did not send as they were */
    writesRefused: SalesforceHealthProblemList;
    auth: {
      failures: number;
      needsReconnect: boolean;
      last: SalesforceHealthProblem | null;
    };
    /**
     * Salesforce has not answered the sync's runs: since when, the last try and
     * how many runs in a row. The sync tries again at `times.nextRunAt`, waiting
     * longer each time up to an hour. Null otherwise. Older answers have none.
     */
    unreachable?: {
      since: string;
      lastAt: string;
      runs: number;
    } | null;
    /**
     * The last write flush ended with Encharge edits still not written to
     * Salesforce; null when it finished. `since` is when a flush first ended for
     * this reason (ISO). `records` is the marked people plus companies, each kind
     * counted up to 1000; null when the count failed. `more` is true when a kind
     * had 1000 or more. Older answers have none.
     */
    writesWaiting?: {
      reason: "not_connected" | "waited_too_long" | "stuck";
      since: string;
      records: number | null;
      more: boolean;
    } | null;
    /** The Opportunity stage and Campaign triggers. Older answers have none. */
    triggers?: {
      /** Flows that wait for an Opportunity stage or Campaign that Salesforce no longer lists */
      missing: SalesforceHealthProblemList;
      /** People on a changed Opportunity or new Campaign member who are not in Encharge, in the last 24 hours */
      skippedNotInEncharge: number;
    };
    /** The Salesforce steps in flows. Older answers have none. */
    steps?: {
      /** Set while the steps wait for the org's API allowance */
      hold: {
        reason: "usage" | "paused";
        percent: number | null;
        until?: string;
        message: string;
      } | null;
      /** Steps that ended with a refusal in the last 24 hours */
      failed: SalesforceHealthProblemList;
      /** Loops between flows that Encharge cut in the last hour */
      loopsStopped: SalesforceHealthProblemList;
    };
  };
  generatedAt: string;
}

/** What POST /sync-now answers. `result` is the one the screen words itself. */
export type SalesforceSyncNowResult =
  | "queued"
  | "already_running"
  | "not_available"
  /** A new connection whose customer has not finished the setup: nothing is read until then */
  | "awaiting_setup"
  | "paused"
  | "needs_reconnect";

export interface SalesforceSyncNowResponse {
  result: SalesforceSyncNowResult;
  queued: boolean;
  /** The server's own plain sentence for it */
  message: string;
}

/**
 * What POST /campaigns/check-now answers (Check now).
 *  requested         a check is owed and runs at the next sync run
 *  joined            a check was already owed: this press is the same request
 *  refused           it would take the day past 80 percent of the allowance: nothing was asked for
 *  nothing_to_check  no Campaign has finished its first read
 *  not_available     Campaigns are not being copied
 */
export type SalesforceCampaignCheckNowResult =
  | "requested"
  | "joined"
  | "refused"
  | "nothing_to_check"
  | "not_available";

export interface SalesforceCampaignCheckNowResponse {
  result: SalesforceCampaignCheckNowResult;
  /** The server's own plain sentence for it */
  message: string;
  calls: number | null;
  afterShare: number | null;
}

/** What POST /opt-out/resend answers (Send again, item 3). */
export type SalesforceResendOptOutsResult =
  | "queued"
  | "nothing"
  | "needs_reconnect"
  | "paused"
  | "switched_off";

export interface SalesforceResendOptOutsResponse {
  result: SalesforceResendOptOutsResult;
  /** The people whose unsubscribe is sent again; 0 unless queued */
  people: number;
  /** The server's own plain sentence for it */
  message: string;
}

/** An error from one of these routes. `status` is the HTTP status, when there was one. */
export type SalesforceSyncRequestError = Error & { status?: number };

const asRequestError = (
  e: any,
  fallback: string
): SalesforceSyncRequestError => {
  const error: SalesforceSyncRequestError = new Error(
    e?.response?.data?.error?.message || e?.message || fallback
  );
  error.status = e?.response?.status;
  return error;
};

export const call = async <T>(
  config: {
    url: string;
    method: "GET" | "POST";
    data?: unknown;
    /** Aborts the request when the caller no longer wants the answer */
    signal?: AbortSignal;
  },
  fallback: string
): Promise<T> => {
  try {
    const res: AxiosResponse<T> = await request({
      ...config,
      withCredentials: true,
      validateStatus: (status) => status < 300,
    });
    return res.data;
  } catch (e) {
    throw asRequestError(e, fallback);
  }
};

// ---------------------------------------------------------------------------
// Customer routes (every mode for /settings, v2 only for the rest)

/**
 * GET /connect-info. The sync an account will be on once Salesforce is
 * connected: the stored one for an account that had Salesforce before, else v2
 * when new connections go to v2, else v1. Needs no Salesforce login, so a drawer
 * that has not connected yet can ask it. Encharge's own rows only.
 */
export interface SalesforceConnectInfo {
  mode: SalesforceSyncMode;
  /**
   * The new sync (v2) only, which is the only one that offers a Sandbox: the
   * Salesforce the sync last read (a Disconnect removes the login and leaves
   * this behind), or null for an account that never connected.
   */
  last?: SalesforceLastConnection | null;
  /** The new sync only: the people the account holds and the plan's limit */
  people?: SalesforcePeopleAndLimit;
  /**
   * The new sync only: the flows, steps and segments that name Campaigns now,
   * which a switch to another Salesforce leaves to check. Null when none could be
   * found out (or the account never connected).
   */
  naming?: SalesforceSwitchScan | null;
}

/** The Salesforce an account used before: Production or a Sandbox, and what is known of the org. */
export interface SalesforceLastConnection {
  environment: SalesforceEnvironment;
  /** Known for a Sandbox, whose connect reads the org's name */
  orgName: string | null;
  /** The host of the org's address (no path), or null */
  host: string | null;
}

/** The people the account holds and how many its plan covers */
export interface SalesforcePeopleAndLimit {
  count: number;
  limit: number;
}

export const getSalesforceConnectInfo = () =>
  call<SalesforceConnectInfo>(
    { url: `${base}/connect-info`, method: "GET" },
    "We couldn't tell which Salesforce sync this account gets."
  );

/**
 * GET /settings/start-estimate. How many Leads and Contacts Salesforce holds
 * that could become people, for the Start card of a Sandbox: two COUNT() calls
 * to Salesforce. `salesforce` is an upper bound (the rules of step 2 are judged
 * after the read, and a person who is both a Lead and a Contact counts twice).
 */
export type SalesforceStartEstimateResult =
  | "counted"
  | "paused"
  | "needs_reconnect"
  | "not_available"
  | "field_refused"
  | "failed";

export interface SalesforceStartEstimate {
  result: SalesforceStartEstimateResult;
  /** The server's own sentence for every result but `counted` */
  message: string;
  /** Encharge's own rows: in every answer */
  people: SalesforcePeopleAndLimit;
  salesforce?: { leads: number; contacts: number; total: number };
  apiCalls: number;
}

export const getSalesforceStartEstimate = () =>
  call<SalesforceStartEstimate>(
    { url: `${base}/settings/start-estimate`, method: "GET" },
    "We couldn't count the people in Salesforce."
  );

export const getSalesforceSyncSettings = () =>
  call<SalesforceSyncSettings>(
    { url: `${base}/settings`, method: "GET" },
    "We couldn't load your Salesforce sync settings."
  );

export const getSalesforceSyncHealth = () =>
  call<SalesforceSyncHealth>(
    { url: `${base}/health`, method: "GET" },
    "We couldn't load the Salesforce sync health."
  );

export const postSalesforceSyncNow = () =>
  call<SalesforceSyncNowResponse>(
    { url: `${base}/sync-now`, method: "POST" },
    "We couldn't start a Salesforce sync."
  );

export const postSalesforceCampaignCheckNow = () =>
  call<SalesforceCampaignCheckNowResponse>(
    { url: `${base}/campaigns/check-now`, method: "POST" },
    "We couldn't ask for the Campaign check."
  );

export const postSalesforceResendOptOuts = () =>
  call<SalesforceResendOptOutsResponse>(
    { url: `${base}/opt-out/resend`, method: "POST" },
    "We couldn't send the unsubscribes again."
  );

// ---------------------------------------------------------------------------
// Admin routes (Encharge staff). The account is the one in the path, never the
// staff member's own.

export const SHADOW_KINDS = [
  "personWouldBeCreated",
  "fieldWouldChange",
  "fieldSkipped",
  "unsubscribeWouldApply",
  "resubscribeWouldApply",
  "unsubscribeKept",
  "deletedInSalesforceKept",
  "mergeWouldMoveId",
  "eventWouldEmit",
  "companyWouldBeCreated",
  "companyWouldBeUpdated",
  "companyFieldWouldChange",
  "companyFieldSkipped",
  "companyDeletedKept",
  "companyWouldBeMerged",
  "linkWouldBeAdded",
  "linkWouldBeRemoved",
  "personCopyWouldChange",
] as const;
export type SalesforceShadowKind = typeof SHADOW_KINDS[number];
export type SalesforceShadowReport = SalesforceReportReadout<
  SalesforceShadowKind
>;

/** A field that starts as Salesforce on purpose though the old sync wrote it back (item 8). */
export interface SalesforceSafeStartField {
  object: "Lead" | "Contact" | "Account";
  salesforceField: string;
}

export interface SalesforceAdminShadowResponse {
  accountId: number;
  mode: SalesforceSyncMode;
  report: SalesforceShadowReport;
  /** Item 8; an older server sends none */
  safeStart?: SalesforceSafeStartField[];
}

/**
 * One place where a fresh conversion of the account's old sync mapping differs
 * from the field mapping the customer saved on the new sync (switchMappingCheck).
 * `v1` is what the conversion says now, `v2` what the saved mapping says.
 */
export interface SalesforceMappingDifference {
  object: "Lead" | "Contact" | "Account";
  salesforceField: string;
  change: "only_in_v1" | "only_in_v2" | "target" | "direction";
  v1?: {
    target: string;
    direction: "salesforce" | "encharge" | "both" | "none";
  };
  v2?: {
    target: string;
    direction: "salesforce" | "encharge" | "both" | "none";
  };
}

/** What the switch to v2 will do with the account's field mapping. */
export interface SalesforceSwitchMappingCheck {
  /**
   * none: no new sync mapping yet (the switch converts); untouched_conversion:
   * built again at the switch; customer_mapping: kept as it is
   */
  kind: "none" | "untouched_conversion" | "customer_mapping";
  /** customer_mapping only: the first few differences */
  differences: SalesforceMappingDifference[];
  differenceCount: number;
  /** the switch needs `keepMapping: true` */
  needsKeep: boolean;
}

/** What the mode route answers when the switch to v2 is asked without `confirm: true`. */
export interface SalesforceModeConfirmationRequired {
  changed: false;
  confirmationRequired: true;
  from: SalesforceSyncMode;
  to: SalesforceSyncMode;
  shadowRan: boolean;
  /** The last shadow report; null when shadow has not run */
  shadow: SalesforceShadowReport | null;
  /** Said when shadow has not run */
  message?: string;
  /** What the switch does with the field mapping; left out by an older server */
  mapping?: SalesforceSwitchMappingCheck;
}

/** A rule of the older list v1 will use. */
export interface SalesforceOlderRule {
  propertyName: string;
  condition: string;
  propertyValue?: string;
  propertyValues?: string[];
}

/** What v1 will do with one object's Sync criteria when the account goes back to it. */
export interface SalesforceRulesForV1 {
  /** The object's rules use groups or Keep out rules, which v1 cannot read */
  usesRules: boolean;
  /** The list v1 will use instead: the rules of the older list that sits beside the groups. Empty: every record. */
  olderList: SalesforceOlderRule[];
  /** The sentence for staff, when the object uses groups or Keep out rules; else null */
  message: string | null;
}

/**
 * What the mode route answers when the move from v2 back to v1 is asked without
 * `confirm: true` and an object's Sync criteria use groups or Keep out rules: v1
 * cannot read them and will use the older list beside them, or let every record
 * in when there is none. Nothing is changed; the same call with `confirm: true`
 * moves the account, and the groups and Keep out rules stay stored.
 */
export interface SalesforceModeBackToV1Confirmation {
  changed: false;
  confirmationRequired: true;
  from: "v2";
  to: "v1";
  criteria: { Lead: SalesforceRulesForV1; Contact: SalesforceRulesForV1 };
  /** The sentences of the objects that use groups or Keep out rules, joined */
  message: string;
}

export const isBackToV1Confirmation = (
  answer: SalesforceModeAnswer
): answer is SalesforceModeBackToV1Confirmation =>
  // the switch to v2 is the only other question the route asks
  answer.confirmationRequired === true && answer.to === "v1";

export interface SalesforceModeChanged {
  changed: boolean;
  confirmationRequired: false;
  from: SalesforceSyncMode;
  to: SalesforceSyncMode;
  reapplied?: boolean;
}

export type SalesforceModeAnswer =
  | SalesforceModeConfirmationRequired
  | SalesforceModeBackToV1Confirmation
  | SalesforceModeChanged;

export const adminBase = (accountId: number) =>
  `${base}/admin/accounts/${encodeURIComponent(String(accountId))}`;

export const getSalesforceAdminShadow = (accountId: number) =>
  call<SalesforceAdminShadowResponse>(
    { url: `${adminBase(accountId)}/shadow`, method: "GET" },
    "We couldn't load the Salesforce shadow report."
  );

/** GET /admin/accounts/:id/v2-flows (item 9). */
export interface SalesforceAdminV2Flows {
  accountId: number;
  /** Live flows with a Salesforce step */
  stepFlows: number;
  /** Live flows that start from a Salesforce Opportunity or Campaign trigger */
  triggerFlows: number;
}

export const getSalesforceAdminV2Flows = (accountId: number) =>
  call<SalesforceAdminV2Flows>(
    { url: `${adminBase(accountId)}/v2-flows`, method: "GET" },
    "We couldn't count the flows that use the new sync."
  );

export const setSalesforceAdminMode = (
  accountId: number,
  mode: SalesforceSyncMode,
  confirm?: boolean,
  keepMapping?: boolean
) =>
  call<SalesforceModeAnswer>(
    {
      url: `${adminBase(accountId)}/mode`,
      method: "POST",
      data: {
        mode,
        ...(confirm === undefined ? {} : { confirm }),
        // true only once staff chose to keep the customer's new sync mapping
        ...(keepMapping === true ? { keepMapping: true } : {}),
      },
    },
    "We couldn't change the Salesforce sync mode."
  );
